// firstRun.js — pure helpers for the brand-new-account Home experience. Framework-free
// so they are unit-tested directly; Scorify.jsx renders from them.

// "Good morning/afternoon/evening, <first name>". hour is 0-23 (local).
export function greetingFor(hour, name) {
  const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const first = String(name || '').trim().split(/\s+/)[0] || '';
  return `Good ${part}${first ? `, ${first}` : ''}`;
}

// The local hour (0-23) in an IANA zone; falls back to the device clock on any failure.
// The user's timezone IS the device zone in practice (they hold the phone); a stored
// profile zone can be passed when known.
export function hourInZone(date = new Date(), zone) {
  try {
    const s = new Intl.DateTimeFormat('en-US', { timeZone: zone || undefined, hour: 'numeric', hour12: false }).format(date);
    let h = parseInt(s, 10);
    if (!Number.isFinite(h)) return date.getHours();
    return h === 24 ? 0 : h;
  } catch {
    return date.getHours();
  }
}

// First-run Home treatment (greeting → brief → rings) lasts the first 7 days OR until a
// pillar earns a real score — whichever comes first. Demo is never first-run.
export function isFirstRunHome({ accountAgeDays, anyPillarScored, isDemo } = {}) {
  if (isDemo) return false;
  const young = accountAgeDays == null || accountAgeDays < 7;
  return young && !anyPillarScored;
}

// A pillar shows a number only once it has real data (pct 0 is real data; null is not).
export function ringDisplay(pillar) {
  const started = !!pillar && pillar.pct !== null && pillar.pct !== undefined;
  return { started, text: started ? `${pillar.pct}%` : 'Not started' };
}

// The center ring is a welcome state until at least one pillar has a score.
export const anyPillarScored = (pillars) => (pillars || []).some((p) => p && p.pct !== null && p.pct !== undefined);

// The three getting-started actions, ticked from real client signals.
export function gettingStartedSteps({ habitLogged, moodLogged, planMade } = {}) {
  return [
    { id: 'habit', label: 'Log a habit', done: !!habitLogged },
    { id: 'mood', label: 'Log your mood', done: !!moodLogged },
    { id: 'plan', label: 'Tell Kai a plan', done: !!planMade },
  ];
}

// The card shows while the account is young (first 7 days) and not all three are done.
export function gettingStartedVisible({ steps, accountAgeDays } = {}) {
  const list = steps || [];
  const allDone = list.length > 0 && list.every((s) => s.done);
  const young = accountAgeDays == null || accountAgeDays < 7;
  return young && !allDone;
}

// Deterministic short welcome shown in the brief card for a brand-new account (no AI,
// never blank) — used when there is no generated briefing yet.
export function welcomeBrief(name) {
  const first = String(name || '').trim().split(/\s+/)[0] || '';
  return {
    title: `Welcome to Scorify${first ? `, ${first}` : ''}`,
    lines: [
      'This is your daily brief. It fills in as you start tracking.',
      'Log a habit, log your mood, or tell me a plan to get going.',
    ],
  };
}
